import React from 'react';

// Must match app/config.py BILLING_MARKUP and LLM_PRICING_GBP (RDF-Agent).
const BILLING_MARKUP = 2.50;

const PRICING_GBP: Record<string, { input: number; output: number }> = {
  'claude-haiku-4-5-20251001': { input: 0.79   * BILLING_MARKUP, output: 3.95   * BILLING_MARKUP },
  'claude-sonnet-4-6':         { input: 2.37   * BILLING_MARKUP, output: 11.85  * BILLING_MARKUP },
  'claude-opus-4-6':           { input: 3.95   * BILLING_MARKUP, output: 19.75  * BILLING_MARKUP },
  'gemini-flash-lite-latest':  { input: 0.237  * BILLING_MARKUP, output: 1.975  * BILLING_MARKUP },
  'gemini-flash-latest':       { input: 1.185  * BILLING_MARKUP, output: 5.925  * BILLING_MARKUP },
  'gemini-pro-latest':         { input: 1.58   * BILLING_MARKUP, output: 9.48   * BILLING_MARKUP },
};

function calcCost(model: string, inTok: number, outTok: number): number {
  const p = PRICING_GBP[model] || PRICING_GBP['claude-sonnet-4-6'];
  return (inTok / 1_000_000) * p.input + (outTok / 1_000_000) * p.output;
}

function fmtGbp(cost: number): string {
  return cost < 0.0005 ? '< £0.001' : `£${cost.toFixed(4)}`;
}

function fmtPct(cost: number, capGbp: number): string {
  if (!capGbp) return '…';
  return `~${((cost / capGbp) * 100).toFixed(5)}%`;
}

interface Props {
  isSuperAdmin: boolean;
  onSelect: (model: string | null) => void;
  orgSpend: { spend_cap_gbp: number } | null;
}

// Fixed estimates matching the agent's app.js approach
const EST_INPUT = 11250;
const EST_OUTPUT = 1500;

const MODELS = [
  { id: 'gemini-flash-lite-latest',  label: 'Gemini Flash Lite', provider: 'gemini', desc: 'Light & cheap' },
  { id: 'gemini-flash-latest',       label: 'Gemini Flash',      provider: 'gemini', desc: 'Fast & smart' },
  { id: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5', provider: 'claude', desc: 'Fast & affordable', superAdminOnly: true },
  { id: 'gemini-pro-latest',         label: 'Gemini Pro',        provider: 'gemini', desc: 'High quality' },
  { id: 'claude-sonnet-4-6',         label: 'Claude Sonnet 4.6', provider: 'claude', desc: 'Balanced quality', superAdminOnly: true },
  { id: 'claude-opus-4-6',           label: 'Claude Opus 4.6',   provider: 'claude', desc: 'Most capable', superAdminOnly: true },
];

export const LlmModal: React.FC<Props> = ({ isSuperAdmin, onSelect, orgSpend }) => {
  return (
    <div className="ai-modal-overlay" onClick={() => onSelect(null)}>
      <div className="ai-llm-modal" onClick={e => e.stopPropagation()}>
        <div className="ai-llm-modal-header">
          <h3>Choose AI Model</h3>
          <p>Select a model for this query</p>
        </div>
        <div className="ai-llm-grid">
          {MODELS.filter(m => !m.superAdminOnly || isSuperAdmin).map(m => {
            const cost = calcCost(m.id, EST_INPUT, EST_OUTPUT);
            return (
              <button
                key={m.id}
                className={`ai-llm-card ai-llm-card-${m.provider}`}
                onClick={() => onSelect(m.id)}
              >
                <div className="ai-llm-card-name">{m.label}</div>
                <div className="ai-llm-card-desc">{m.desc}</div>
                {isSuperAdmin ? (
                  <div className="ai-llm-card-cost">{fmtGbp(cost)} <span style={{ fontWeight: 400, opacity: 0.65, fontSize: '0.85em' }}>per query est.</span></div>
                ) : (
                  <div className="ai-llm-card-cost">{fmtPct(cost, orgSpend?.spend_cap_gbp ?? 0)} <span style={{ fontWeight: 400, opacity: 0.65, fontSize: '0.85em' }}>of allowance</span></div>
                )}
              </button>
            );
          })}
        </div>
        <button className="ai-llm-cancel" onClick={() => onSelect(null)}>Cancel</button>
      </div>
    </div>
  );
};
